import { Record } from "immutable";
import { createSelector } from "reselect";
import { getTime, isAfter } from "date-fns";

import {
  Job,
  UI_STATE_ENUM,
  TYPE_ENUM,
  JOB_FETCH_TYPE,
  JobsMap,
} from "@typings/jobs";

import { IStore } from "@app/redux/reducers";

export const jobFinishTime = (item: Record<Job>): Date => {
  const execDate = new Date(item.get("executionDate"));
  const duration = Number(item.get("duration"));
  execDate.setHours(execDate.getHours() + duration);
  return execDate;
};

export const isJobCurrentlyActive = (item: Record<Job>): boolean => {
  const minute = 360000;
  const finish = jobFinishTime(item).getTime();
  const current = new Date().getTime();
  const isNotCancelled = item.get("uiState") !== UI_STATE_ENUM.CANCELLED;

  const offset = Math.abs(new Date().getTimezoneOffset()) * minute;
  const start = getTime(new Date(item.get("executionDate"))) + offset;

  return isNotCancelled && start <= current && current < finish;
};

const getAllJobs2ByLocationFn = (store: IStore) =>
  store.jobs2.get("jobsByLocation");

export const getAllJobs2ByLocation = createSelector(
  getAllJobs2ByLocationFn,
  f => f,
);

// filtering by uiState
const getJobs2ByLocationFn = (
  store: IStore,
  locationUuid: string,
  showPrevious: boolean,
): JobsMap | undefined => {
  const uiState = showPrevious
    ? JOB_FETCH_TYPE.PREVIOUS
    : JOB_FETCH_TYPE.UPCOMING;

  const result = store.jobs2.getIn(["jobsByLocation", locationUuid, uiState]);
  return result;
};
export const getJobs2ByLocation = createSelector(getJobs2ByLocationFn, f => f);

const getJobs2LocationFn = (store: IStore, locationUuid: string) =>
  store.jobs2.get("jobsByLocation").get(locationUuid);
export const getJobs2Location = createSelector(getJobs2LocationFn, f => f);

const hasJobs2ToFetchFn = (
  store: IStore,
  locationUuid: string,
  upcoming?: boolean,
) =>
  store.jobs2
    .get("jobsByLocation")
    .get(locationUuid)
    ?.get(upcoming ? "hasNextPageUpcoming" : "hasNextPagePrevious") ?? false;
export const hasMoreJobs2ToFetch = createSelector(hasJobs2ToFetchFn, f => f);

const getAllJobs2Fn = (store: IStore) => store.jobs2.get("allJobs");
export const getAllJobs2 = createSelector(getAllJobs2Fn, f => f);

const getNextUpcomingJobs2Fn = (store: IStore) =>
  store.jobs2.get("nextUpcomingJob");
export const getNextUpcomingJobs2 = createSelector(
  getNextUpcomingJobs2Fn,
  f => f,
);

const getJobFunction = (store: IStore, job: string) =>
  store.jobs2.get("allJobs").get(job)!;
export const getJob = createSelector(getJobFunction, f => f);

const getJobActionsFunction = (store: IStore, job: string) =>
  store.jobs2.get("allJobs").get(job)?.get("actions");
export const getJobActions = createSelector(getJobActionsFunction, f => f);

const hasUpcomingJob2Fn = (store: IStore) =>
  store.jobs2
    .get("allJobs")
    .filter(job => job.get("uiState") !== UI_STATE_ENUM.CANCELLED).size > 0;
export const getHasUpcomingJob2 = createSelector(hasUpcomingJob2Fn, f => f);

// filtering by date
const getAllJobsByStateFn = (store: IStore, showUpcoming: boolean) => {
  return store.jobs2.get("allJobs").filter((job: Record<Job>) => {
    const endDate = jobFinishTime(job);
    const isUpcoming = isAfter(endDate, new Date());

    return showUpcoming ? isUpcoming : !isUpcoming;
  });
};

export const getAllJobsByState = createSelector(getAllJobsByStateFn, f => f);

const getJobTypeFunction = (store: IStore, jobId: string): TYPE_ENUM =>
  store.jobs2.getIn(["allJobs", jobId, "type"]);
export const getJobType = createSelector(getJobTypeFunction, f => f);

const getJobLocationIdFn = (store: IStore, jobId: string): TYPE_ENUM =>
  store.jobs2.getIn(["allJobs", jobId, "locationUuid"]);
export const getJobLocationId = createSelector(getJobLocationIdFn, f => f);

/*
  Cancellation
*/

const getCancellationReasonsFunction = (store: IStore, jobId: string) =>
  store.jobs2.getIn(["allJobs", jobId, "cancellationProcess", "reasons"]);
export const getCancellationReasons = createSelector(
  getCancellationReasonsFunction,
  f => f,
);

const getCancellationUuidFunction = (store: IStore, jobId: string) =>
  store.jobs2.getIn([
    "allJobs",
    jobId,
    "cancellationProcess",
    "cancellationRequestUuid",
  ]);
export const getCancellationUuid = createSelector(
  getCancellationUuidFunction,
  f => f,
);

const getCancellationStateFunction = (store: IStore, jobId: string) =>
  store.jobs2.get("allJobs").get(jobId)?.get("cancellationState");
export const getCancellationState = createSelector(
  getCancellationStateFunction,
  f => f,
);

const getCancellationProgressFunction = (store: IStore, jobId: string) =>
  store.jobs2.get("allJobs").get(jobId)?.get("cancellationProcess");
export const getCancellationProgress = createSelector(
  getCancellationProgressFunction,
  f => f,
);

export const getCancellationFeeLevel = (store: IStore, jobId: string) =>
  store.jobs2
    .get("allJobs")
    .get(jobId)
    ?.get("cancellationProcess")
    ?.get("feeLevel");

const getFreeCancellationsLeftFunction = (store: IStore, jobId: string) =>
  store.jobs2.getIn([
    "allJobs",
    jobId,
    "cancellationProcess",
    "freeCancellationsLeft",
  ]);
export const getFreeCancellationsLeft = createSelector(
  getFreeCancellationsLeftFunction,
  f => f,
);

const getLateJobCancellationThresholdFn = (store: IStore, jobId: string) =>
  store.jobs2
    .get("allJobs")
    .get(jobId)
    ?.get("cancellationProcess")
    ?.get("lateJobCancellationThreshold");
export const getLateJobCancellationThreshold = createSelector(
  getLateJobCancellationThresholdFn,
  f => f,
);

const getJobs2LoadingStateFunction = (store: IStore) =>
  store.jobs2.get("loadingState");
export const getJobs2LoadingState = createSelector(
  getJobs2LoadingStateFunction,
  f => f,
);

const hasPendingHolidaysFunction = (store: IStore): boolean => {
  return store.jobs2.get("holidaysResponsePendingJobs") > 0;
};
export const hasPendingHolidays = createSelector(
  hasPendingHolidaysFunction,
  f => f,
);

const checkIfJobExistsFunction = (store: IStore, id: string): boolean => {
  return typeof store.jobs2.getIn(["allJobs", id]) !== "undefined";
};
export const checkIfJobExists = createSelector(
  checkIfJobExistsFunction,
  f => f,
);

export const getCancellationCustomReasons = createSelector(
  getCancellationProgressFunction,
  f => f?.get("customReasons"),
);

export const getCancellationCustomReason = (store: IStore) =>
  store.jobs2.get("cancellationReason");

export const getCancellationEditUrl = createSelector(
  getCancellationProgressFunction,
  f => f?.get("changeRequestRelativeUrl"),
);

export const getReschedulingData = (store: IStore) =>
  store.jobs2.get("rescheduling");

export const getReschedulingTimeslotsData = (store: IStore) =>
  store.jobs2.get("rescheduling").get("timeslots");

export const getPreferredAgentSlots = (store: IStore) => {
  const timeslots = store.jobs2.get("rescheduling").get("timeslots");

  return timeslots
    .get("availabilities")
    ?.find(el => el.agent.uuid === timeslots.get("preferredAgentUuid"))?.slots;
};

export const getAgentReplacementData = (store: IStore) =>
  store.jobs2.get("agentReplacement");

export const getAgentReplacementChangeRequestUrl = createSelector(
  getAgentReplacementData,
  replacement => replacement?.get("changeRequestRelativeUrl"),
);

const hasUpcomingB2BJobsFn = (store: IStore) => {
  return store.jobs2
    .get("allJobs")
    .some(
      job =>
        job.get("uiState") === UI_STATE_ENUM.UPCOMING &&
        job.get("type") === TYPE_ENUM.B2B_CLEANING,
    );
};
export const hasUpcomingB2BJobs = createSelector(hasUpcomingB2BJobsFn, f => f);

// Beyond planning window
const getJobs2ByLocationBeyondPlanningWindowFn = (
  store: IStore,
  locationUuid: string,
) => store.jobs2.get("jobsByLocationBeyondPlanningWindow").get(locationUuid);
export const getJobs2ByLocationBeyondPlanningWindow = createSelector(
  getJobs2ByLocationBeyondPlanningWindowFn,
  f => f,
);

const getJobs2BeyondPlanningWindowByLocationFn = (
  store: IStore,
  locationUuid: string,
) =>
  store.jobs2
    .get("jobsByLocationBeyondPlanningWindow")
    .get(locationUuid)
    ?.get("jobs");
export const getJobs2BeyondPlanningWindowByLocation = createSelector(
  getJobs2BeyondPlanningWindowByLocationFn,
  f => f,
);

const getJobs2BeyondPlanningWindowHasNextPageFn = (
  store: IStore,
  locationUuid: string,
): boolean =>
  !!store.jobs2
    .get("jobsByLocationBeyondPlanningWindow")
    .get(locationUuid)
    ?.get("hasNextPage");
export const getJobs2BeyondPlanningWindowHasNextPage = createSelector(
  getJobs2BeyondPlanningWindowHasNextPageFn,
  f => f,
);

const getJobs2BeyondPlanningWindowLoadingStateFunction = (store: IStore) =>
  store.jobs2.get("loadingStateBeyondPlanningWindow");
export const getJobs2BeyondPlanningWindowLoadingState = createSelector(
  getJobs2BeyondPlanningWindowLoadingStateFunction,
  f => f,
);
